/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useStagingRows, useUpdateDecision, useUpdateStaging, useCommitImport, ACTION_TO_RPC } from './hooks/useDevImportClassify';
import { STAGE_OPTIONS } from './devImportUtils';
import { DevImportSummaryCards } from './DevImportSummaryCards';
import { DevConflictCard } from './DevConflictCard';
import styles from './DevImport.module.css';

const ICON_X = <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>;
const ICON_WARN = <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" /></svg>;

const FILTER_TO_CLASSIFICATION = {
  new: 'new',
  exact: 'exact_duplicate',
  soft: 'soft_conflict',
  internal: 'internal_duplicate',
  invalid: 'invalid',
};

const BULK_OPTIONS_REGULAR = [
  { id: '', label: 'Bulk action…' },
  { id: 'skip', label: 'Mark all as Skip' },
  { id: 'replace', label: 'Mark all as Replace' },
  { id: 'merge', label: 'Mark all as Merge' },
  { id: 'review_later', label: 'Mark all as Review later' },
];

export function DevImportConflictModal({ session, filename, uploadedBy, batchId, summary, onClose, onCommitted, showToast }) {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [decisions, setDecisions] = useState(() => ({}));
  const [bulkAction, setBulkAction] = useState('');

  const { data: rows = [], isLoading } = useStagingRows(session);
  const updateDecision = useUpdateDecision();
  const updateStaging  = useUpdateStaging();
  const commitMut = useCommitImport();

  // T-1039 — backward-compat banner: STAGE column was absent from the Excel.
  // Per-row but per-file-constant (every row in a file shares the same value).
  // We read from rows[0]; if rows haven't loaded yet, suppress the banner.
  const stageColumnMissing = rows.length > 0 && rows[0]?.raw_data?._stage_was_missing === true;

  // T-1039 — STAGE override handler. Mutation is awaited so the user can
  // see the in-flight state via updateStaging.isPending (disables the select).
  const handleStageChange = useCallback(async (stagingId, newStageKey) => {
    const opt = STAGE_OPTIONS.find(o => o.key === newStageKey);
    if (!opt) return;
    try {
      await updateStaging.mutateAsync({
        stagingId,
        rawDataPatch: {
          stage_status: opt.stage_status,
          full_status:  opt.full_status,
          // Override = explicit valid choice; amber chip clears.
          // _stage_excel_raw preserved as historical context.
          _stage_was_valid: true,
        },
      });
    } catch (err) {
      if (showToast) showToast('Stage update failed: ' + (err?.message || 'unknown'), true);
    }
  }, [updateStaging, showToast]);

  // Auto-select default decisions on first load.
  useEffect(() => {
    if (!rows.length || Object.keys(decisions).length) return;
    const initial = {};
    rows.forEach(r => {
      if (r.user_action) {
        const uiAction = Object.entries(ACTION_TO_RPC).find(([_, rpc]) => rpc === r.user_action)?.[0];
        if (uiAction) initial[r.staging_id] = uiAction;
      } else if (r.classification === 'new') {
        initial[r.staging_id] = 'import';
      } else if (r.classification === 'exact_duplicate' || r.classification === 'invalid') {
        initial[r.staging_id] = 'skip';
      }
    });
    setDecisions(initial);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape key closes
  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape') handleClose(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = rows;
    if (activeFilter !== 'all') {
      const cls = FILTER_TO_CLASSIFICATION[activeFilter];
      if (cls) list = list.filter(r => r.classification === cls);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) list = list.filter(r => String(r.raw_data?.dev_number || r.raw_data?.item_code || '').toLowerCase().includes(q));
    return list;
  }, [rows, activeFilter, searchQuery]);

  const decisionCounts = useMemo(() => {
    const out = { import: 0, skip: 0, replace: 0, merge: 0, review_later: 0, pending: 0 };
    rows.forEach(r => {
      const d = decisions[r.staging_id];
      if (!d) out.pending++;
      else if (out[d] !== undefined) out[d]++;
    });
    return out;
  }, [rows, decisions]);

  function handleAction(stagingId, action) {
    setDecisions(prev => ({ ...prev, [stagingId]: action }));
    const rpcAction = ACTION_TO_RPC[action];
    if (rpcAction) updateDecision.mutate({ stagingId, userAction: rpcAction });
  }

  function handleBulkApply() {
    if (!bulkAction) return;
    const next = { ...decisions };
    filtered.forEach(r => {
      // Don't override invalid rows — they can only Skip.
      if (r.classification === 'invalid' && bulkAction !== 'skip') return;
      // Don't override 'new' rows with conflict-only actions.
      if (r.classification === 'new' && bulkAction !== 'skip') {
        next[r.staging_id] = 'import';
      } else {
        next[r.staging_id] = bulkAction;
      }
      const rpcAction = ACTION_TO_RPC[next[r.staging_id]];
      if (rpcAction) updateDecision.mutate({ stagingId: r.staging_id, userAction: rpcAction });
    });
    setDecisions(next);
    setBulkAction('');
  }

  async function handleCommit() {
    const decisionsArr = rows
      .filter(r => decisions[r.staging_id])
      .map(r => ({ staging_id: r.staging_id, user_action: ACTION_TO_RPC[decisions[r.staging_id]] }));
    try {
      const result = await commitMut.mutateAsync({ session, decisions: decisionsArr, batchId });
      if (showToast) {
        showToast(`Imported ${result?.inserted ?? 0} new · Replaced ${result?.replaced ?? 0} · Skipped ${result?.skipped ?? 0}${result?.failed ? ` · ${result.failed} failed` : ''}`);
      }
      if (onCommitted) onCommitted(result);
    } catch (err) {
      if (showToast) showToast('Commit failed: ' + (err?.message || 'unknown'), true);
    }
  }

  function handleClose() {
    if (commitMut.isPending) return;
    if (onClose) onClose();
  }

  const commitDisabled = commitMut.isPending || decisionCounts.pending > 0;

  return (
    <div className={`${styles.dna} ${styles.overlay}`} onClick={handleClose}>
      <div className={styles.bigModal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHead}>
          <div>
            <div className={styles.modalTitle}>Import conflicts</div>
            <div className={styles.subMeta}>
              {filename ? <span><strong>{filename}</strong> · </span> : null}
              {uploadedBy ? <span>uploaded by <strong>{uploadedBy}</strong></span> : null}
            </div>
          </div>
          <button onClick={handleClose} className={styles.closeBtn} aria-label="Close">{ICON_X}</button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {stageColumnMissing && (
            <div className={styles.stageBanner}>
              {ICON_WARN}
              <strong>STAGE column not detected</strong>
              <span className={styles.sbSoft}>— all rows defaulted. Use the per-row dropdown to override.</span>
            </div>
          )}
          <DevImportSummaryCards summary={summary} activeFilter={activeFilter} onFilterChange={setActiveFilter} />

          {/* Toolbar */}
          <div className={styles.toolbar}>
            <select value={bulkAction} onChange={e => setBulkAction(e.target.value)} className={styles.select}>
              {BULK_OPTIONS_REGULAR.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <button
              onClick={handleBulkApply}
              disabled={!bulkAction}
              className={`${styles.bulkBtn} ${bulkAction ? styles.bulkBtnActive : ''}`}
            >
              Apply to {filtered.length}
            </button>
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search dev_number…"
              className={styles.searchInput}
            />
          </div>

          {/* List */}
          {isLoading && <div className={styles.loading}>Loading…</div>}
          {!isLoading && filtered.length === 0 && (
            <div className={styles.emptyList}>
              <div className={styles.emptyTitle}>No rows in this view</div>
              <div className={styles.emptySub}>{searchQuery ? 'No matches for your search.' : 'Try a different filter.'}</div>
            </div>
          )}
          {!isLoading && filtered.map(r => (
            <DevConflictCard
              key={r.staging_id}
              stagingRow={r}
              decision={decisions[r.staging_id]}
              onAction={action => handleAction(r.staging_id, action)}
              onStageChange={handleStageChange}
              updateMutation={updateStaging}
            />
          ))}
        </div>

        {/* Footer */}
        <div className={styles.modalFoot}>
          <div className={styles.tally}>
            <strong style={{ color: '#3a8845' }}>{decisionCounts.import}</strong> import ·
            <strong style={{ color: '#14110d' }}>{decisionCounts.replace}</strong> replace ·
            <strong style={{ color: '#14110d' }}>{decisionCounts.merge}</strong> merge ·
            <strong style={{ color: '#8e887b' }}>{decisionCounts.skip}</strong> skip
            {decisionCounts.pending > 0 && (
              <> · <strong style={{ color: '#a86810' }}>{decisionCounts.pending}</strong> pending</>
            )}
          </div>
          <button onClick={handleClose} disabled={commitMut.isPending} className={styles.btnGhost}>Cancel</button>
          <button onClick={handleCommit} disabled={commitDisabled} className={styles.btnPrimary}>
            {commitMut.isPending ? 'Committing…' : `Commit (${decisionCounts.import + decisionCounts.replace + decisionCounts.merge})`}
          </button>
        </div>
      </div>
    </div>
  );
}
